import { FormStatusEnum } from "@/components/molecules/FormWrapper/FormWrapper.types";
import { useEffect, useState } from "react";
import { useClientOrdersData } from "@/modules/client-orders/application/hooks/useClientOrdersData";
import { ClientOrdersForm } from "@/modules/client-orders/presentation/ClientOrdersForm";
import { clientOrderKeys, ClientOrderFormData, clientOrderColumns } from "@/modules/client-orders/infrastructure/ClientOrdersConstants";
import { ClientInstrument, ClientOrderEntity, ClientOrderStatusEnum, ClientOrderTypeEnum, ClientShareInstrument, RentTypeEnum } from "@/modules/client-orders/domain/entities/ClientOrdersEntity";
import { useCreateClientOrder, useDeleteClientOrder, useUpdateClientOrder } from "@/modules/client-orders/application/hooks/useClientOrdersQueries";
import { useTitle } from "@/core/context/TitleContext";
import { useConfirmDialogs, usePromptDialogs } from "@/core/context/DialogContext";
import { Loading } from "@/components/atoms/Loading/Loading";
import { FormHeader } from "@/components/molecules/FormHeader/FormHeader";
import { DataTable } from "@/components/molecules/DataTable/DataTable";
import { useQueryClient } from "@tanstack/react-query";
import { externalApiService } from "@/core/infrastructure/ExternalApiService";
import { LimitTypeEnum } from "@/modules/orders/domain/entities/OrdersEntity";
import { ButtonBase } from "@/components/ui/button";
import { CopyIcon } from "lucide-react";

// Componente funcional
export const ClientOrdersPage = () => {
    const [formStatus, setFormStatus] = useState<FormStatusEnum>(FormStatusEnum.List);
    const [clientInstrumentSelected, setClientInstrumentSelected] = useState<ClientInstrument>();
    const [clientShareInstrumentSelected, setClientShareInstrumentSelected] = useState<ClientShareInstrument>();
    const [clientOrderSelected, setClientOrderSelected] = useState<ClientOrderEntity>();
    const { setTitle } = useTitle();
    const promptDialog = usePromptDialogs();
    const showDialog = useConfirmDialogs();

    const queryClient = useQueryClient();
    const {
        clientOrders,
        clientRates,
        clients,
        clientOptions,
        clientInstrumentOptions,
        clientInstruments,
        clientShareInstruments,
        clientShareInstrumentsOptions,
        portfolios,
        isLoading,
        clientOrdersRefetch,
    } = useClientOrdersData();

    // effects
    useEffect(() => {
        setTitle("Ordenes");
    }, []);

    useEffect(() => {
        clientOrderSelected?.instrument && setClientInstrumentSelected(clientOrderSelected.instrument);
    }, [clientOrderSelected]);

    // hooks
    const createClientOrderMutation = useCreateClientOrder();
    const updateClientOrderMutation = useUpdateClientOrder();
    const deleteClientOrderMutation = useDeleteClientOrder();

    const onChange = (id: string) => {
        setClientInstrumentSelected(clientInstruments.find(instrument => instrument.id === id));
    }

    const onShareChange = (id: string) => {
        setClientShareInstrumentSelected(clientShareInstruments.find(instrument => instrument.id === id));
    }

    const handleCopy = async (value: string) => {
        try {
            await navigator.clipboard.writeText(value);
        } catch (err) {
            console.error("Error al copiar: ", err);
        }
    };

    const onSubmit = async (formData: ClientOrderFormData) => {
        let success = false;
        if (formStatus === FormStatusEnum.Add) {
            const { id, ...payload } = formData;

            const resp = await createClientOrderMutation.mutateAsync({
                ...payload,
                // Ensure backend always receives numeric fields
                rate: payload.rate ?? 0,
                value: payload.value ?? 0,
                limitPrice: payload.limitPrice ?? 0,
                orderType: payload.orderType ? ClientOrderTypeEnum.COMPRA : ClientOrderTypeEnum.VENTA,
                limitType: payload.limitType ? LimitTypeEnum.MERCADO : LimitTypeEnum.LIMITE,
            });
            success = resp.success;
            if (success) {
                const confirmNumber = (resp.data as unknown as ClientOrderEntity).confirmNumber ?? "";
                showDialog({
                    title: "Se ha creado la orden exitosamente!",
                    description: (
                        <div className="flex-col space-y-6 text-center">
                            <div className="flex text-green-500 font-bold text-6xl justify-center gap-2">
                                {confirmNumber}
                                <ButtonBase onClick={() => handleCopy(confirmNumber)}>
                                    <CopyIcon className="text-primary-500" />
                                </ButtonBase>
                            </div>
                            <p>
                                Es muy importante que utilices este código en la referencia de la orden de mercado ya que así podemos monitorear el cumplimiento de las políticas de inversión y facilitar la asifnación y complementación de la orden!
                            </p>
                        </div>
                    ),
                    variant: "success",
                });
            }
        } else {
            const resp = await updateClientOrderMutation.mutateAsync({
                id: formData.id ?? "0",
                ...formData,
                orderType: formData.orderType ? ClientOrderTypeEnum.COMPRA : ClientOrderTypeEnum.VENTA,
                limitType: formData.limitType ? LimitTypeEnum.MERCADO : LimitTypeEnum.LIMITE,
            });
            success = resp.success;
        }
        if (success) {
            await queryClient.invalidateQueries({ queryKey: clientOrderKeys.all });
            handleChangeFormStatus(FormStatusEnum.List);
        }
    };

    const onDelete = async (id: string) => {
        await deleteClientOrderMutation.mutateAsync(id);
        await queryClient.invalidateQueries({ queryKey: clientOrderKeys.all });
        handleChangeFormStatus(FormStatusEnum.List);
    };

    const onContinue = async (formData: ClientOrderFormData, newStatus: ClientOrderStatusEnum) => {
        if (clientOrderSelected) {
            if (newStatus === ClientOrderStatusEnum.COMPLEMENTACION) {
                if (formData.rentType === RentTypeEnum.RV) {
                    await updateClientOrderMutation.mutateAsync({
                        id: formData.id ?? "0",
                        ...formData,
                        status: newStatus,
                        orderType: formData.orderType ? ClientOrderTypeEnum.COMPRA : ClientOrderTypeEnum.VENTA,
                        limitType: formData.limitType ? LimitTypeEnum.MERCADO : LimitTypeEnum.LIMITE,
                    });
                    await queryClient.invalidateQueries({ queryKey: clientOrderKeys.all });
                    setClientOrderSelected(prev => ({
                        ...(prev as ClientOrderEntity),
                        status: ClientOrderStatusEnum.COMPLEMENTACION
                    }));
                } else {
                    const confirmNumber = await promptDialog({
                        title: "Número de operación",
                        description: "Ingresa el número de operación para complementar el registro.",
                        placeholder: "Número de operación",
                        variant: "warning",
                        defaultValue: clientOrderSelected.confirmNumber
                    });
                    if (confirmNumber) {
                        await updateClientOrderMutation.mutateAsync({
                            id: formData.id ?? "0",
                            ...formData,
                            status: newStatus,
                            orderType: formData.orderType ? ClientOrderTypeEnum.COMPRA : ClientOrderTypeEnum.VENTA,
                            limitType: formData.limitType ? LimitTypeEnum.MERCADO : LimitTypeEnum.LIMITE,
                            confirmNumber
                        });
                        await queryClient.invalidateQueries({ queryKey: clientOrderKeys.all });
                        setClientOrderSelected(prev => ({
                            ...(prev as ClientOrderEntity),
                            status: ClientOrderStatusEnum.COMPLEMENTACION,
                            confirmNumber
                        }));
                    }
                }
            } else if (newStatus === ClientOrderStatusEnum.FIN) {
                // When status changes to FIN, call the stored procedure
                console.log("🎯 Status changing to FIN - calling stored procedure...");

                try {
                    // First update the order status
                    await updateClientOrderMutation.mutateAsync({
                        id: formData.id ?? "0",
                        ...formData,
                        status: newStatus,
                        orderType: formData.orderType ? ClientOrderTypeEnum.COMPRA : ClientOrderTypeEnum.VENTA,
                        limitType: formData.limitType ? LimitTypeEnum.MERCADO : LimitTypeEnum.LIMITE,
                    });

                    // Get the instrument name (issuerName) from the selected instrument
                    const instrumentName = clientInstrumentSelected?.issuerName || clientOrderSelected?.instrument?.issuerName || '';

                    // Then call the stored procedure to insert into OperacionesBursatiles
                    const spResult = await externalApiService.excecuteSP(
                        {
                            Id: formData.id,
                            OrderType: formData.orderType ? ClientOrderTypeEnum.COMPRA : ClientOrderTypeEnum.VENTA,
                            Status: newStatus,
                            RentType: formData.rentType,
                            ClientIdentification: formData.clientIdentification,
                            ClientIdentificationType: formData.clientIdentificationType,
                            Instrument: instrumentName,
                            Quantity: formData.quantity,
                            Rate: formData.rate,
                            Value: formData.value,
                            ConfirmNumber: formData.confirmNumber || null,
                            CreatedAt: new Date().toISOString(),
                            UpdatedAt: new Date().toISOString()
                        }
                    );
                    console.log("🎯 Stored procedure executed successfully:", spResult);

                    await queryClient.invalidateQueries({ queryKey: clientOrderKeys.all });
                    await setClientOrderSelected({ ...clientOrderSelected, status: newStatus });
                } catch (error) {
                    console.error("❌ Error in FIN status change:", error);
                    alert(`Error al cambiar el estado a FIN: ${error instanceof Error ? error.message : 'Error desconocido'}`);
                }
            } else {
                await updateClientOrderMutation.mutateAsync({
                    id: formData.id ?? "0",
                    ...formData,
                    status: newStatus,
                    orderType: formData.orderType ? ClientOrderTypeEnum.COMPRA : ClientOrderTypeEnum.VENTA,
                    limitType: formData.limitType ? LimitTypeEnum.MERCADO : LimitTypeEnum.LIMITE,
                });
                await queryClient.invalidateQueries({ queryKey: clientOrderKeys.all });
                await setClientOrderSelected({ ...clientOrderSelected, status: newStatus });
            }

        }
    };

    const handleChangeFormStatus = (statusOrUpdater: FormStatusEnum | ((prevState: FormStatusEnum) => FormStatusEnum)) => {
        const nextStatus = typeof statusOrUpdater === 'function'
            ? (statusOrUpdater as (prev: FormStatusEnum) => FormStatusEnum)(formStatus)
            : statusOrUpdater;

        if (nextStatus === FormStatusEnum.List) {
            setClientOrderSelected(undefined);
            setClientInstrumentSelected(undefined);
        }

        setFormStatus(nextStatus);
    };

    if (
        isLoading ||
        createClientOrderMutation.isPending ||
        updateClientOrderMutation.isPending ||
        deleteClientOrderMutation.isPending
    ) return <Loading />;

    return (
        <>
            <FormHeader
                title="Ordenes de Clientes"
                changeFormStatus={handleChangeFormStatus}
                formStatus={formStatus}
                selectItem={setClientOrderSelected}
                refetch={clientOrdersRefetch}
                canAdd
            />

            {formStatus === FormStatusEnum.List ? (
                <DataTable
                    data={clientOrders}
                    columns={clientOrderColumns}
                    tableHeadClass="bg-primary-500 text-white"
                    pageSize={10}
                    changeFormStatus={setFormStatus}
                    selectItem={setClientOrderSelected}
                    defaultSortKey="createdAt"
                    defaultSortOrder="desc"
                />
            ) : (
                <ClientOrdersForm
                    onSubmit={onSubmit}
                    onDelete={onDelete}
                    onContinue={onContinue}
                    clientRates={clientRates}
                    clients={clients}
                    clientOptions={clientOptions}
                    clientInstrumentOptions={clientInstrumentOptions}
                    clientInstrumentSelected={clientInstrumentSelected}
                    setClientInstrumentSelected={onChange}
                    formStatus={formStatus}
                    clientOrderSelected={clientOrderSelected}
                    clientShareInstrumentOptions={clientShareInstrumentsOptions}
                    clientShareInstrumentSelected={clientShareInstrumentSelected}
                    setClientShareInstrumentSelected={onShareChange}
                    portfolios={portfolios}
                />
            )}
        </>
    );
};
